// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Label } from '../ui/label';
import { EducBox, ResultCard, InputRow, EDUC, calcIRPEF, fmt } from './shared';

const DetrazioniCalc = () => {
  const [reddito, setReddito] = useState('30000');
  const [tipoLav, setTipoLav] = useState('dipendente');
  const [speseMediche, setSpeseMediche] = useState('0');
  const [interessiMutuo, setInteressiMutuo] = useState('0');
  const [speseUniv, setSpeseUniv] = useState('0');
  const [speseScolFigli, setSpeseScolFigli] = useState('0');
  const [abbTrasporti, setAbbTrasporti] = useState('0');
  const [speseRistrutturazione, setSpeseRistrutturazione] = useState('0');
  const [speseEnerg, setSpeseEnerg] = useState('0');
  const [donazioni, setDonazioni] = useState('0');
  const [figliCarico, setFigliCarico] = useState('0');
  const [contribFondo, setContribFondo] = useState('0');

  const R = parseFloat(reddito) || 0;
  const nFigli = parseInt(figliCarico) || 0;
  const fondoDeducibile = Math.min(parseFloat(contribFondo) || 0, 5300);
  const redditoDeducibile = R - fondoDeducibile;
  const irpefLorda = calcIRPEF(redditoDeducibile);

  const detMediche = Math.max(0, (parseFloat(speseMediche) || 0) - 129.11) * 0.19;
  const detMutuo = Math.min(parseFloat(interessiMutuo) || 0, 4000) * 0.19;
  const detUniv = (parseFloat(speseUniv) || 0) * 0.19;
  const detScol = Math.min(parseFloat(speseScolFigli) || 0, 800 * Math.max(1, nFigli)) * 0.19;
  const detTrasporti = Math.min(parseFloat(abbTrasporti) || 0, 250) * 0.19;
  const detRistrutturazione = ((parseFloat(speseRistrutturazione) || 0) * 0.50) / 10;
  const detEnerg = ((parseFloat(speseEnerg) || 0) * 0.65) / 10;
  const detDonazioni = Math.min(parseFloat(donazioni) || 0, R * 0.30) * 0.30;
  const detFigli = R < 80000 ? 950 * nFigli : 0;

  const totaleDetrazioni = detMediche + detMutuo + detUniv + detScol + detTrasporti + detRistrutturazione + detEnerg + detDonazioni + detFigli;
  const rimborso = Math.min(totaleDetrazioni, irpefLorda);
  const irpefNetta = irpefLorda - rimborso;
  const risparmioMensile = rimborso / 12;

  const rows = [
    { voce: 'Spese mediche e sanitarie', spesa: parseFloat(speseMediche) || 0, perc: '19%', risparmio: detMediche },
    { voce: 'Interessi mutuo prima casa', spesa: parseFloat(interessiMutuo) || 0, perc: '19% (max €4.000)', risparmio: detMutuo },
    { voce: 'Spese universitarie', spesa: parseFloat(speseUniv) || 0, perc: '19%', risparmio: detUniv },
    { voce: 'Spese scolastiche figli', spesa: parseFloat(speseScolFigli) || 0, perc: '19% (max €800/figlio)', risparmio: detScol },
    { voce: 'Abbonamento trasporti', spesa: parseFloat(abbTrasporti) || 0, perc: '19% (max €250)', risparmio: detTrasporti },
    { voce: 'Spese ristrutturazione', spesa: parseFloat(speseRistrutturazione) || 0, perc: '50% in 10 anni', risparmio: detRistrutturazione },
    { voce: 'Risparmio energetico', spesa: parseFloat(speseEnerg) || 0, perc: '65% in 10 anni', risparmio: detEnerg },
    { voce: 'Donazioni ONLUS', spesa: parseFloat(donazioni) || 0, perc: '30%', risparmio: detDonazioni },
    { voce: `Figli a carico (${nFigli})`, spesa: 0, perc: '€950/figlio', risparmio: detFigli },
  ].filter(r => r.risparmio > 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.detrazioni} />
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Reddito lordo annuo (€)" value={reddito} onChange={setReddito} prefix="€" />
        <div>
          <Label className="text-[#888780] text-sm mb-1">Tipo lavoratore</Label>
          <div className="flex gap-2 mt-1">
            {[['dipendente', 'Dipendente'], ['autonomo', 'Autonomo'], ['pensionato', 'Pensionato']].map(([v, l]) => (
              <button key={v} onClick={() => setTipoLav(v)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${tipoLav === v ? 'bg-[#3D7A52] text-white' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <InputRow label="Spese mediche e sanitarie (€)" value={speseMediche} onChange={setSpeseMediche} prefix="€" tooltip="Detrazione 19% sulla parte eccedente €129,11" />
        <InputRow label="Interessi mutuo prima casa €/anno" value={interessiMutuo} onChange={setInteressiMutuo} prefix="€" tooltip="Detrazione 19% fino a €4.000 di interessi passivi annui" />
        <InputRow label="Spese universitarie (€)" value={speseUniv} onChange={setSpeseUniv} prefix="€" tooltip="Detrazione 19% per rette universitarie" />
        <InputRow label="Spese scolastiche figli (€)" value={speseScolFigli} onChange={setSpeseScolFigli} prefix="€" tooltip="Detrazione 19% fino a €800/figlio" />
        <InputRow label="Abbonamento trasporti pubblici (€)" value={abbTrasporti} onChange={setAbbTrasporti} prefix="€" tooltip="Detrazione 19% fino a €250 di spesa annua" />
        <InputRow label="Spese ristrutturazione edilizia (€)" value={speseRistrutturazione} onChange={setSpeseRistrutturazione} prefix="€" tooltip="Detrazione 50% in 10 anni fino a €96.000" />
        <InputRow label="Spese risparmio energetico (€)" value={speseEnerg} onChange={setSpeseEnerg} prefix="€" tooltip="Detrazione 65% in 10 anni ecobonus" />
        <InputRow label="Erogazioni liberali / donazioni (€)" value={donazioni} onChange={setDonazioni} prefix="€" tooltip="Detrazione 30% per ONLUS fino al 30% del reddito" />
        <InputRow label="Figli a carico (numero)" value={figliCarico} onChange={setFigliCarico} suffix="figli" tooltip="Detrazione fino a €950/figlio under 21" />
        <InputRow label="Contributi fondo pensione (€)" value={contribFondo} onChange={setContribFondo} prefix="€" tooltip="Deduzione fino a €5.300/anno (Legge Bilancio 2026) — riduce il reddito imponibile" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <ResultCard label="Rimborso IRPEF stimato" value={`€${fmt(rimborso)}`} green data-testid="detrazioni-rimborso" />
        <ResultCard label="Risparmio mensile equivalente" value={`€${fmt(risparmioMensile)}`} gold />
        <ResultCard label="IRPEF lorda" value={`€${fmt(irpefLorda)}`} />
        <ResultCard label="IRPEF netta effettiva" value={`€${fmt(irpefNetta)}`} green />
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm" data-testid="detrazioni-table">
            <thead>
              <tr className="text-[#888780] border-b border-white/[0.06]">
                <th className="text-left py-2">Voce</th>
                <th className="text-right py-2">Spesa inserita</th>
                <th className="text-right py-2">Detrazione</th>
                <th className="text-right py-2 text-[#3D7A52]">Risparmio fiscale</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-white/[0.04]">
                  <td className="py-2 text-[#E8E8E4]">{row.voce}</td>
                  <td className="text-right py-2 text-[#888780]">{row.spesa > 0 ? `€${fmt(row.spesa)}` : '—'}</td>
                  <td className="text-right py-2 text-[#888780]">{row.perc}</td>
                  <td className="text-right py-2 text-[#3D7A52] font-semibold">€{fmt(row.risparmio)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#3D7A52]/30">
                <td colSpan={3} className="py-2 font-semibold text-[#E8E8E4]">Totale detrazioni</td>
                <td className="text-right py-2 font-bold text-[#3D7A52]">€{fmt(totaleDetrazioni)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-[#888780] mt-4 p-3 bg-[#252523] rounded-xl" data-testid="detrazioni-disclaimer">
        Calcolo indicativo basato su aliquote 2026. Le detrazioni sono soggette a limiti, franchigie e condizioni specifiche. Per la dichiarazione ufficiale rivolgiti a un CAF o consulente fiscale.
      </p>
      {/* Cross-link to GuidaDichiarazioneRedditi (Deployment 13) */}
      <a href="/guida-dichiarazione-redditi" data-testid="link-guida-dichiarazione"
        className="mt-4 block p-4 rounded-xl text-sm transition-colors hover:bg-white/[0.04]"
        style={{ background: '#252523', border: '1px solid rgba(201,168,76,0.3)' }}>
        <p className="font-semibold text-[#E8E8E4]">Hai bisogno di aiuto con la dichiarazione? →</p>
        <p className="text-xs text-[#C9A84C] mt-0.5">Leggi la guida completa</p>
      </a>
</motion.div>
  );
};

export default DetrazioniCalc;
